import React from "react";
// import { useState } from "react"; // Eski statik web widget için kullanılıyordu
import type { AppProps } from "next/app";
import { Provider } from "react-redux";
import { appWithTranslation } from 'next-i18next';
// import { useTranslation } from 'next-i18next'; // Eski statik web widget için kullanılıyordu
import Head from "next/head";
import NProgress from "nprogress";
import { Poppins } from "next/font/google";
// import dynamic from "next/dynamic"; // Eski statik web widget için kullanılıyordu
import store from "@/redux/app/store";
import Script from "next/script";
import * as gtag from "../../lib/gtag";
import "@/styles/globals.css";
import "../base/prototype/string";
import { MenuOpenProvider } from "@/context/MenuOpenContext";

// Eski statik web widget — ServiceStone widget loader'ı ile değiştirildi.
// const WebWidget = dynamic(() => import("@/components/web-widget/WebWidget"), {
//   ssr: false,
//   loading: () => null,
// });

const SERVICESTONE_WIDGET_SRC = "https://base.stoneity.com/servicestone/widget/loader.js";
const SERVICESTONE_WIDGET_KEY = "wk_3db50c64ddcf4d78b91e2f7b9e874d33";
const SERVICESTONE_WIDGET_LAUNCHER_ID = "sty-widget-launcher";

const poppins = Poppins({
  weight: ['300', '400', '500', '600', '700', '900'],
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  fallback: ['system-ui', 'arial'],
});

const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID;
const AW_KEY = process.env.NEXT_PUBLIC_AW_KEY;
const isProduction = process.env.NODE_ENV === "production";

NProgress.configure({
  showSpinner: false,
});

function App({ Component, pageProps, router }: AppProps) {
  // const [shouldLoadWidget, setShouldLoadWidget] = useState(false);

  React.useEffect(() => {
    const handleRouteStart = () => NProgress.start();
    const handleRouteDone = (url: URL) => {
      NProgress.done();

      if (isProduction) {
        gtag.pageview(url);
      }
    }

    router.events.on("routeChangeStart", handleRouteStart);
    router.events.on("routeChangeComplete", handleRouteDone);
    router.events.on("routeChangeError", handleRouteDone);

    router.beforePopState((state) => {
      state.options.scroll = false;

      return true;
    });

    return () => {
      // Make sure to remove the event handler on unmount!
      router.events.off("routeChangeStart", handleRouteStart);
      router.events.off("routeChangeComplete", handleRouteDone);
      router.events.off("routeChangeError", handleRouteDone);
    };
  }, [router]);

  // Eski statik web widget lazy-load mantığı
  // React.useEffect(() => {
  //   const loadWidget = () => setShouldLoadWidget(true);
  //   let idleCallbackId: number | null = null;
  //   let timeoutId: ReturnType<typeof setTimeout> | null = null;
  //   const passiveOptions = { passive: true } as AddEventListenerOptions;
  //
  //   if (typeof window !== "undefined" && "requestIdleCallback" in window) {
  //     idleCallbackId = window.requestIdleCallback(loadWidget, { timeout: 3000 });
  //   } else {
  //     timeoutId = setTimeout(loadWidget, 2000);
  //   }
  //
  //   window.addEventListener("pointerdown", loadWidget, passiveOptions);
  //   window.addEventListener("keydown", loadWidget);
  //   window.addEventListener("touchstart", loadWidget, passiveOptions);
  //   window.addEventListener("scroll", loadWidget, passiveOptions);
  //
  //   return () => {
  //     if (idleCallbackId !== null && typeof window !== "undefined" && "cancelIdleCallback" in window) {
  //       window.cancelIdleCallback(idleCallbackId);
  //     }
  //
  //     if (timeoutId !== null) {
  //       clearTimeout(timeoutId);
  //     }
  //
  //     window.removeEventListener("pointerdown", loadWidget, passiveOptions);
  //     window.removeEventListener("keydown", loadWidget);
  //     window.removeEventListener("touchstart", loadWidget, passiveOptions);
  //     window.removeEventListener("scroll", loadWidget, passiveOptions);
  //   };
  // }, []);

  // Canonical URL for EN and TR locales
  const baseUrl = 'https://stoneity.com';
  const locale = router.locale || 'en';
  const currentPath = router.asPath;

  // Build canonical URL based on locale
  let canonicalUrl = baseUrl;
  if (locale === 'tr') {
    canonicalUrl = `${baseUrl}/tr${currentPath === '/' ? '' : currentPath}`;
  } else if (locale === 'en') {
    // For EN, use base URL without /en prefix (default locale)
    canonicalUrl = `${baseUrl}${currentPath === '/' ? '' : currentPath}`;
  } else {
    canonicalUrl = `${baseUrl}${currentPath === '/' ? '' : currentPath}`;
  }

  // Eski statik web widget state'i
  // const [widgetMenuOpen, setWidgetMenuOpen] = useState(false);
  // const { t } = useTranslation("common");

  // ServiceStone widget'ı dışarıya açma API'si sunmuyor; sayfalardaki "Talk with us"
  // butonları launcher'a tıklayarak widget'ı açar.
  const setWidgetMenuOpen = React.useCallback((open: boolean) => {
    const launcher = document.getElementById(SERVICESTONE_WIDGET_LAUNCHER_ID);
    if (!launcher) return;

    const isOpen = launcher.getAttribute("aria-expanded") === "true";
    if (isOpen !== open) {
      launcher.click();
    }
  }, []);

  return (
    <div className={poppins.className}>
      <Head>
        <title>Stoneity</title>
        {(locale === 'tr' || locale === 'en') && (
          <link key="canonical" rel="canonical" href={canonicalUrl} />
        )}
      </Head>
      {isProduction && GA_TRACKING_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${GA_TRACKING_ID}');
          ${AW_KEY ? `gtag('config', '${AW_KEY}');` : ""}
        `}
          </Script>
        </>
      )}
      <Provider store={store}>
        <MenuOpenProvider>
          <Component
            {...pageProps}
            setWidgetMenuOpen={setWidgetMenuOpen}
          />
        </MenuOpenProvider>
      </Provider>
      {/* Eski statik web widget */}
      {/* {shouldLoadWidget && (
        <WebWidget
          widgetMenuOpen={widgetMenuOpen}
          setWidgetMenuOpen={setWidgetMenuOpen}
          t={t}
        />
      )} */}
      <Script
        id="servicestone-widget-loader"
        src={SERVICESTONE_WIDGET_SRC}
        data-widget-key={SERVICESTONE_WIDGET_KEY}
        strategy="lazyOnload"
      />
    </div>
  );
}

export default appWithTranslation(App);
